import React, {useContext, useEffect, useState} from "react"
import {CategoryHelper, CategoryHelperContext} from "../../services/helpers/category-helper"
import useSpendingData from "../dashboard/insight/use-spending-data"
import {DefaultExcludeCategoryIds} from "../dashboard/insight/category-settings/category-settings"
import {Category} from "../../domain/category"
import {formatImpact} from "../dashboard/transactions/transaction-amount/transaction-amount"
import {toAmount, toFormattedNumber, toPercentage} from "../../components-shared/formatted-number/formatted-number"
import styles from "./climate-report.module.scss"
import {Heading} from "../../components-shared/heading/heading"
import {FormTextInput} from "../../components-shared/forms/form-text-input/form-text-input-view"
import {invalidValidationResult, ValidationResult} from "../../util/validation/validation-result"
import {gaSendClimateReportEvent} from "../../services/analytics/google-analytics-helper"
import {sum} from "../../util/compute-utils"

const InitialFamilyMemberCount = 2
const AverageImpactPerDutchPerson = 14000

export const ClimateReport: React.FC = () => {
  const categoryHelper = useContext<CategoryHelper | null>(CategoryHelperContext)
  const applicableCategoriesValue = categoryHelper === null ? [] : categoryHelper.categories.filter(x => DefaultExcludeCategoryIds.indexOf(x.id) === -1)
  const [applicableCategories, setApplicableCategories] = useState<Category[]>(applicableCategoriesValue)
  const [familyMemberCountEditableValue, setFamilyMemberCountEditableValue] = useState<string>(InitialFamilyMemberCount.toString())
  const [, allSpendingData,] = useSpendingData("lastYear", applicableCategories)

  const [familyMemberCount, familyMemberCountValidationResult] = parseAndValidateFamilyMemberCount(familyMemberCountEditableValue)

  useEffect(() => {
    if (categoryHelper === null) return

    setApplicableCategories(categoryHelper.categories.filter(x => DefaultExcludeCategoryIds.indexOf(x.id) === -1))
  }, [categoryHelper])

  if (allSpendingData === null) return null

  const months = allSpendingData.monthlySpendingData.months.length
  const enoughMonths = months >= 6
  const enoughForComparison = months >= 18
  const currentYearMonths = allSpendingData.monthlySpendingData.months.slice(-12)
  const previousYearMonths = allSpendingData.monthlySpendingData.months.slice(-24, -12)

  const co2CurrentYear = currentYearMonths.map(month => allSpendingData.totalCo2ByMonth[month.index]).reduce(sum, 0)
  const co2PreviousYear = previousYearMonths.map(month => allSpendingData.totalCo2ByMonth[month.index]).reduce(sum, 0)
  const improvement = (co2CurrentYear / co2PreviousYear) - 1

  const kmCount = co2CurrentYear * 10
  const treeCount = co2CurrentYear / 1000 * 50
  const ticketCount = co2CurrentYear / 4000

  const co2PerPerson = co2CurrentYear / familyMemberCount
  const comparedToAveragePerson = (co2PerPerson / AverageImpactPerDutchPerson) - 1

  if (!enoughMonths) return <div className={styles.ClimateReport}>Importeer tenminste 6 volledige maanden aan transacties om jouw impact te kunnen berekenen.</div>

  const changeFamilyMemberCount = (value: string) => {
    setFamilyMemberCountEditableValue(value)
    gaSendClimateReportEvent("Edit family member count")
  }

  return <div className={styles.ClimateReport}>
    <NumberSection>
      <Co2Number impact={co2CurrentYear}/>
      <p>totale klimaatimpact van de laatste 12 maanden
        in kilogram CO2-equivalenten</p>
    </NumberSection>

    {enoughForComparison
      ? <NumberSection>
        <RedGreenPercentage factor={improvement}/>
        <p>{improvement <= 0 ? "minder" : "meer"} dan het jaar ervoor</p>
      </NumberSection>
      : <p>Importeer tenminste 18 volledige maanden aan transacties om te kunnen vergelijken met het jaar ervoor</p>}

    <p>Je totale impact staat gelijk aan:</p>

    <NumberSection>
      <LargeNumber amount={kmCount}>km</LargeNumber>
      <p>autorijden met een middenklasser (0,1 kg/km)</p>
    </NumberSection>

    <NumberSection>
      <LargeNumber amount={treeCount}>bomen</LargeNumber>
      <p>die een jaar groeien (50 bomen per 1000 kg)</p>
    </NumberSection>

    <NumberSection>
      <LargeNumber amount={ticketCount}>vliegtickets naar Azië</LargeNumber>
      <p>Een retourtje Thailand staat gelijk aan 4000 kg CO2</p>
    </NumberSection>


    <Heading size="large" className={styles.PerPersonHeader}>Per persoon</Heading>
    <FormTextInput label="Aantal personen in je gezin" value={familyMemberCountEditableValue} onChange={e => changeFamilyMemberCount(e.target.value)} showValidity={true} validationResult={familyMemberCountValidationResult}/>

    <NumberSection>
      <Co2Number impact={co2PerPerson}/>
      <p>per persoon<br/>
        op basis van {familyMemberCount} personen</p>
    </NumberSection>

    <NumberSection>
      <RedGreenPercentage factor={comparedToAveragePerson}/>
      <p>{comparedToAveragePerson <= 0 ? "minder" : "meer"} in vergelijking met de gemiddelde Nederlander
        De wereldwijde klimaatimpact van een gemiddelde Nederlandse consument in 2019 was 14.000 kg CO2
        Dit is gebaseerd op CBS met aftrek van 3.000 kg CO2 vanwege overheidsimpact (kantoren, wegen, zorg)</p>
    </NumberSection>


    <Heading size="medium">Wat kost {formatImpact(co2PerPerson, 0)} CO<sub>2</sub>?</Heading>
    <NumberSection>
      <div className={styles.LargeNumber}>{toAmount(10 / 1000 * co2PerPerson, 0)} - {toAmount(20 / 1000 * co2PerPerson, 0)}</div>
      <p>kost het om jouw persoonlijke impact te compenseren via duurzame projecten in ontwikkelingslanden
        Afhankelijk van het soort project dat je kiest, kost het €10 tot €20 per 1000kg,
        via <a href="https://marketplace.goldstandard.org/collections/projects" target="_blank" rel="noopener noreferrer">Gold Standard</a> certificering
        (bewijs dat het echt gebeurt).</p>
    </NumberSection>

    <NumberSection>
      <Cost amount={28 / 1000 * co2PerPerson}/>
      <p>betalen alle grote bedrijven in de EU om jouw jaarlijkse CO2 te mogen uitstoten met hun fabriek
        (<a href="https://ember-climate.org/data/carbon-price-viewer/" target="_blank" rel="noopener noreferrer">€28 per 1000 kg</a>)
        Als de overheid de afgesproken CO2-belasting invoert, komt dat voor Nederlandse bedrijven hier bovenop.</p>
    </NumberSection>

    <NumberSection>
      <Cost amount={110 / 1000 * co2PerPerson}/>
      <p>rekent TruePrice aan maatschappelijke kosten voor schade en herstel van jouw CO2 uitstoot
        (<a href="https://trueprice.org/a-roadmap-for-true-pricing/" target="_blank" rel="noopener noreferrer">€110 per 1000 kg</a>)</p>
    </NumberSection>
  </div>
}

const parseAndValidateFamilyMemberCount = (value: string): [number, ValidationResult] => {
  try {
    const numberValue = parseInt(value)
    if (numberValue.toString() !== value) return [InitialFamilyMemberCount, invalidValidationResult("Vul alstublieft een geheel getal in")]
    if (numberValue < 1) return [InitialFamilyMemberCount, invalidValidationResult("Vul alstublieft tenminste 1 persoon in")]
    return [numberValue, {valid: true}]
  } catch (err) {
    return [InitialFamilyMemberCount, invalidValidationResult("Vul alstublieft een geldig getal in")]
  }
}


const NumberSection: React.FC = ({children}) => {
  return <div className={styles.NumberSection}>{children}</div>
}


interface Co2NumberProps {
  impact: number
}
const Co2Number: React.FC<Co2NumberProps> = ({impact}) => {
  return <span className={styles.Co2Number}>{formatImpact(impact, 0)} CO<sub>2</sub></span>
}


interface CostProps {
  amount: number
}
const Cost: React.FC<CostProps> = ({amount}) => {
  return <span className={styles.LargeNumber}>{toAmount(amount, 0)}</span>
}


interface LargeNumberProps {
  amount: number
}
const LargeNumber: React.FC<LargeNumberProps> = ({amount, children}) => {
  return <span className={styles.LargeNumber}>{toFormattedNumber(amount, 0)} {children}</span>
}


interface RedGreenPercentageProps {
  factor: number
}
const RedGreenPercentage: React.FC<RedGreenPercentageProps> = ({factor}) => {
  const className = factor <= 0 ? styles.PercentagePositive : styles.PercentageNegative

  return <span className={className}>{toPercentage(Math.abs(factor), 0)}</span>
}
